import EditControls from '@creator/ui/components/Contenteditable/EditControls';
import useContentEditable from '@creator/ui/hooks/useContentEditable';
import { cn } from '@creator/ui/utils/ui';
import QuickCommentInput from '@src/basic-components/CommentInput/QuickCommentInput';
import ProfileImageWithLink from '@src/components/ProfileImage/ProfileImageWithLink';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { ensurePostUrl } from '@src/model/upvote/helpers';
import { getUserId } from '@src/model/user/helpers';
import { editComment } from '@src/store/comments/edit';
import { useComment } from '@src/store/comments/hooks/useComment';
import type { LinkMeta } from '@src/types/link-meta';
import type { FC } from 'react';
import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import CommentActions from '../CommentActions/CommentActions';
import CommentBasicInfo from './CommentBasicInfo';
import CommentContent from './CommentContent';
import Replies from './Replies';

export interface CommentProps {
    commentId: string;
    className?: string;
    tokenName: string;
    postId?: string;
    size?: 'sm' | 'md' | 'lg';
    isHighlighted?: boolean;
    includeReplies?: boolean;
    parentCommentId?: string;
    hideStatus?: boolean;
    goToCommentOnClick?: boolean;
}

const Comment: FC<CommentProps> = props => {
    const { className = '', goToCommentOnClick, commentId, hideStatus, parentCommentId, postId, includeReplies = false, tokenName, isHighlighted } = props;
    const comment = useComment(commentId);
    const hasReplies = comment?.numComments && comment.numComments > 0;
    const commentsRef = useRef<HTMLDivElement>(null);
    const commentUrl = `/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId || comment?.postId || '')}/comment/${commentId}`;
    const [isSaving, setIsSaving] = useState(false);
    const [editLinkMeta, setEditLinkMeta] = useState<LinkMeta | null | 'hidden'>(null);
    const navigate = useNavigate();

    useLayoutEffect(() => {
        if (!isHighlighted || !commentId) return;
        // Scroll to the comment when it is highlighted
        setTimeout(() => {
            commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500); // Timeout to ensure the scroll happens after the DOM update
    }, [commentId, isHighlighted]);

    const { isEditMode, draft, isDraftValid, enterEditMode, exitEditMode, handleChange, handleKeyDown, handleBlur, save, cancel } = useContentEditable({
        initialValue: comment?.content || '',
        onSave: async (content: string) => {
            setIsSaving(true);
            try {
                await editComment({
                    tokenName,
                    userId: getUserId(),
                    commentId,
                    content,
                    linkMeta: editLinkMeta && editLinkMeta !== 'hidden' ? editLinkMeta : null,
                });
                exitEditMode();
                setEditLinkMeta(null);
            } catch (error) {
                console.error('Edit failed:', error);
                alert((error as Error).message);
            } finally {
                setIsSaving(false);
            }
        },
    });

    // Initialize editLinkMeta with comment's linkMeta when entering edit mode
    useLayoutEffect(() => {
        if (isEditMode && !editLinkMeta && editLinkMeta !== 'hidden') {
            setEditLinkMeta(comment?.linkMeta || null);
        }
    }, [isEditMode, comment?.linkMeta]);

    const onReplyClick = useCallback(() => {
        // This is no longer needed since reply input is always shown
    }, []);

    const onClick = useCallback(() => {
        if (!goToCommentOnClick) return;
        navigate(commentUrl);
    }, [goToCommentOnClick, navigate, commentUrl]);

    function getBaseClassName() {
        const { isHighlighted } = props;
        const base = 'transition space-y-2 space-x-3 flex px-4 py-3 cursor-pointer';
        if (isHighlighted) return `${base} animate-hl`;
        if (isEditMode && !parentCommentId) return `${base} bg-gray-200 dark:bg-gray-800`;

        return base;
    }

    function getProfileImageClassName() {
        const { size = 'lg' } = props;
        if (size === 'sm') return 'h-6 w-6';
        if (size === 'md') return 'h-7 w-7';
        return 'h-8 w-8';
    }

    const renderReplies = () => {
        if (!hasReplies || !includeReplies || !postId) return null;
        return <Replies postId={postId} tokenName={tokenName} parentCommentId={commentId} className="mt-3 -mx-4" />;
    };

    const renderProfileImage = () => {
        if (!comment) return null;
        return (
            <ProfileImageWithLink
                className="max-h-fit"
                profileImageProps={{
                    tokenName,
                    showBadge: true,
                    displayName: comment.publisherName,
                    className: getProfileImageClassName(),
                }}
            />
        );
    };

    const renderEditControls = () => {
        if (!isEditMode) return;
        return <EditControls isSaveDisabled={!isDraftValid()} isSaving={isSaving} onSave={save} onCancel={cancel} className="text-gray-500" />;
    };

    const renderCommentActions = () => {
        if (isEditMode) return;
        return <CommentActions onEditClick={enterEditMode} className="text-gray-500" tokenName={tokenName} commentId={commentId} onReplyClick={onReplyClick} />;
    };

    function renderQuickCommentInput() {
        const { postId, parentCommentId } = props;
        if (!postId) return null;
        // If this is a reply (has parentCommentId), send the parent's ID to keep replies flat
        // Otherwise, send this comment's ID as the parent
        const replyToId = parentCommentId || commentId;
        return <QuickCommentInput postId={postId} replyToCommentId={replyToId} tokenName={tokenName} />;
    }

    return (
        <div onClick={onClick} ref={commentsRef} className={cn(getBaseClassName(), className)} data-testid="Comment" data-comment-id={commentId}>
            {renderProfileImage()}
            <div className="flex-1 text-sm">
                <div className="space-y-2">
                    <CommentBasicInfo commentId={commentId} tokenName={tokenName} />
                    <CommentContent
                        hideStatus={hideStatus}
                        isEditMode={isEditMode}
                        draft={draft}
                        commentId={commentId}
                        tokenName={tokenName}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                    />
                    {renderEditControls()}
                    {renderCommentActions()}
                    {renderQuickCommentInput()}
                </div>
                {renderReplies()}
            </div>
        </div>
    );
};

export default memo(Comment);
